import { createContext, useContext, useEffect, useState, useCallback, useRef } from "react";
import { ToastContext } from "cai-fusion";
import { useNotificationApiService } from "../hooks/useNotificationApi";
import { useAuthentication } from "./AuthContext";
import { useSignalR } from "../components/symphony/contexts/SignalRContext";
import { debugLog } from "../configs/loggingConfig";

const NotificationContext = createContext();

function NotificationProvider({ children }) {
    const notificationServiceApi = useNotificationApiService();
    const { createInfoToast } = useContext(ToastContext);
    const { apiAccessToken } = useAuthentication();
    const [notifications, setNotifications] = useState([]);
    const [selectedNotifications, setSelectedNotifications] = useState([]);
    const [unreadCount, setUnreadCount] = useState(0);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const notificationsRef = useRef();


    const { addMappingMethod } = useSignalR();


    useEffect(() => {
        loadNotifications();
        addMappingMethod(
            {
                //This method will be called when a new Notification is received
                TextName: "ReceiveNotification",
                Method: receiveNotification,
            }
        );
        addMappingMethod(
            {
                //This method will be called when a new Notification is marked as read
                TextName: "ReceiveNotificationRead",
                Method: receiveNotificationRead,
            }
        );
        addMappingMethod(
            {
                //This method will be called when a new Notification is deleted
                TextName: "ReceiveNotificationDelete",
                Method: receiveNotificationDelete,
            }
        );
    }, []);

    useEffect(() => {
        notificationsRef.current = notifications;
        setUnreadCount(notifications?.filter((notif) => !notif.markedReadUtc).length);
        
    }, [notifications]);


     // signalR responses methods for notification updates
    const receiveNotification = async (notifID) => {
        debugLog.log(`[SIGNALR] Received SignalR notification mapped to ReceiveNotification, notifID: `, notifID);
        await retrieveNotification(notifID);
        notificationToast(notifID);
    }

    const receiveNotificationRead = async (notifIDs) => {
        debugLog.log(`[SIGNALR] Received SignalR notification mapped to ReceiveNotificationRead, notifIDs: `, notifIDs);
        debugLog.log("[NOTIF] Notification IDs to Mark as Read: ", notifIDs, " length: ", notifIDs.length);
        debugLog.log("[NOTIF] current notifications: ", notifications);
        setNotifications([...notificationsRef.current.map((notif) => notifIDs.includes(notif.id)
            ? {...notif, markedReadUtc : Date.now()}
            : notif )]);
    }

    const receiveNotificationDelete = async (notifIDs) => {
        debugLog.log(`[SIGNALR] Received SignalR notification mapped to ReceiveNotificationDelete, notifIDs: `, notifIDs);
        debugLog.log("[NOTIF] Notification IDs to Delete: ", notifIDs, " length: ", notifIDs.length);
        debugLog.log("[NOTIF] current notifications: ", notificationsRef);
        setNotifications(notificationsRef.current.filter((notif) => !notifIDs.includes(notif.id)));
    }

    const notificationToast = (notifID) => {
        try {
            const notification = notificationsRef.current.find((notif) => notif.id == notifID);
            debugLog.log("[NOTIF] Notification Object: " + notification);
            createInfoToast(
                <div>
                        <h4>{notification.title}</h4>
                        <p>{notification.body}</p>
                </div>
            );
        } catch (error) {
            debugLog.error("[NOTIFS] An error occurred when sending a notification toast for notification ", notifID, error);
        }
        
    }

    const retrieveNotification = useCallback(async (notifID) => {
        setLoading(true);
        setError(null);
        try {
            const notif = await notificationServiceApi.getNotification(notifID);
            const newNotif = [...notificationsRef.current, ...notif];
            debugLog.log("[NOTIF] retrieving new notifs: ", newNotif);
            setNotifications(newNotif);
            notificationsRef.current = newNotif;
        } catch (error) {
            debugLog.error("[NOTIFS] An error occurred when loading notification ", notifID, " from the backend.", error);
            setError(error);
        } finally {
            setLoading(false);
        }
    }, [notificationServiceApi]);

    const loadNotifications = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const response = await notificationServiceApi.getNotifications();
            debugLog.log("[NOTIF LOAD] response: ", response);
            setNotifications(response);
        } catch (error) {
            debugLog.error("[NOTIFS] An error occurred when loading notifications from the backend.", error);
            setError(error);
        } finally {
            setLoading(false);
        }
    }, [notificationServiceApi]);

    const toggleSelectNotification = useCallback((notifID) => {
        setSelectedNotifications((prevSelected) =>
            prevSelected.includes(notifID)
                ? prevSelected.filter((id) => id !== notifID)
                : [...prevSelected, notifID]
        );
    }, []);

    const markNotificationsAsRead = useCallback(async (notifs) => {
        const notificationsToBeRead = notifs ?? selectedNotifications;
        debugLog.log("[NOTIF] toberead: ", notificationsToBeRead);
        debugLog.log("[NOTIF] all notifications: ", notifications);
        try {
            await notificationServiceApi.markNotificationsAsRead(notificationsToBeRead);
            const newNotifs = notificationsRef.current.map((notif) =>
                    notificationsToBeRead.includes(notif.id) ? { ...notif, markedReadUtc: Date.now() } : notif
                );
            setNotifications(newNotifs);
            notificationsRef.current = newNotifs;
            setSelectedNotifications([]);
        } catch (error) {
            debugLog.error("[NOTIFS] Something went wrong when trying to mark notifications as read!", error);
            setError(error);
        }
    }, [selectedNotifications, notifications, setNotifications, notificationServiceApi]);

    const deleteNotifications = useCallback(async (notifs) => {
        const notificationsToBeDeleted = notifs ?? selectedNotifications;
        debugLog.log("[NOTIF] tobedeleted: ", notificationsToBeDeleted);
        debugLog.log("[NOTIF] all notifications: ", notifications);
        try {
            await notificationServiceApi.deleteNotifications(notificationsToBeDeleted);
            const newNotifs = notificationsRef.current.filter((notif) => !notificationsToBeDeleted.includes(notif.id));
            setNotifications(newNotifs);
            notificationsRef.current = newNotifs;
        } catch (error) {
            debugLog.error("[NOTIFS] Something went wrong when trying to delete notifications!", error);
            setError(error);
        } 
    }, [selectedNotifications, notifications, setNotifications, notificationServiceApi]);

    return (
        <NotificationContext.Provider
            value={{
                notifications,
                selectedNotifications,
                unreadCount,
                loading,
                error,
                loadNotifications,
                toggleSelectNotification,
                markNotificationsAsRead,
                deleteNotifications,
                notificationToast
            }}
        >
            {children}
        </NotificationContext.Provider>
    );
}

function useNotification() {
    const context = useContext(NotificationContext);
    if (context === undefined) {
        throw new Error("useNotification must be used within a NotificationProvider.");
    }
    return context;
}

export { NotificationProvider, useNotification };